import { useState } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import qs from 'query-string';
import { Link, useLocation } from 'react-router-dom';
import { css } from 'styled-components';

import { DeepLinkCampaign, DeepLinkChannel, useCreateDeeplink } from 'src/app/hooks/data/deeplinks';
import { usePrimaryPatient } from 'src/app/hooks/data/patient';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { medications } from 'src/app/router/paths';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import LinkButton from '../../Elements/Button/LinkButton';
import CapsuleIcon from '../../Elements/CapsuleIcon';
import Spinner from '../../Elements/Spinner';
import { CTAGroup } from '../../FeatureLayouts/SimpleFeatureLayout/CTAGroup';

export const OpenAppLanding = () => {
  useSegmentEvent('appAdoptionView', { shouldTrackOnMount: true });
  const trackEventStayOnWeb = useSegmentEvent('appAdoptionStayOnWeb', { shouldTrackOnMount: false });
  const trackEventCheckoutInApp = useSegmentEvent('appAdoptionOpenInApp', { shouldTrackOnMount: false });

  const location = useLocation();
  const { userUuid: qsUserUuid } = qs.parse(location.search);

  const [isFetchingDeeplink, setIsFetchingDeeplink] = useState(false);
  const { mutateAsync: createDeeplink, isLoading: isCreateDeeplinkLoading } = useCreateDeeplink();

  const { data: patient, isLoading: isPatientLoading } = usePrimaryPatient();

  const navigateToDeeplink = async () => {
    setIsFetchingDeeplink(true);
    try {
      trackEventCheckoutInApp();
      const deeplink = await createDeeplink({
        deeplinkPath: 'home',
        campaign: DeepLinkCampaign.mobile_first_time_checkout,
        channel: DeepLinkChannel.sms,
        userUuid: qsUserUuid as string,
      });
      window.open(deeplink.url, '_self');
    } catch (e) {
      setIsFetchingDeeplink(false);
    }
  };

  if (isCreateDeeplinkLoading || isFetchingDeeplink || isPatientLoading) {
    return <Spinner centered />;
  }

  return (
    <div
      css={css`
        background-color: ${({ theme }) => theme.colors.white};
        text-align: center;
        overflow: hidden;
      `}
    >
      <div
        css={css`
          height: 74px;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 0px 0.5px 0px 0px #b6bfce;
        `}
      >
        <Link
          css={css`
            display: block;
            margin-left: auto;
            margin-right: auto;
          `}
          aria-label="Capsule Home"
          data-cy="logo"
          to={medications.toPath()}
        >
          <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
        </Link>
      </div>
      <div
        css={css`
          padding-top: 48px;
        `}
      >
        <img
          css={css`
            transform: scale(1.5);
            height: 8.4rem;
            margin: 0 auto 24px auto;
          `}
          src={'/public/modal-heart.svg'}
          alt=""
        />
        {!!patient && <Typography variant={'heading2'}>Hi {patient.firstName},</Typography>}
        <Typography
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s3};
          `}
          variant={'heading2'}
        >
          Welcome to Capsule
        </Typography>
        <Typography
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s2};
          `}
          variant={'body'}
        >
          Get the app to order your medications
        </Typography>
        <div
          css={css`
            position: fixed;
            bottom: 0;
            z-index: 1;
            width: 100%;
          `}
        >
          <CTAGroup
            css={css`
              box-shadow: none;
            `}
            cta1={{
              children: 'Order in app',
              onClick: navigateToDeeplink,
            }}
            cta2={
              <LinkButton
                to={medications.toPath()}
                onClick={() => trackEventStayOnWeb()}
                size="medium"
                style="tertiary"
              >
                Stay on web
              </LinkButton>
            }
          />
        </div>
      </div>
    </div>
  );
};
